<script setup lang="ts">
import { computed, toValue } from 'vue'

import { SportId } from '@collector/sportsapi-client-legacy'
import Button from '@mobile/components/Button/Button.vue'
import { resolveEventSelectOptions } from '@mobile/views/Relation/DetailsPanel/resolveEventSelectOptions'
import { useSportOptions } from '@mobile/views/Relation/DetailsPanel/Sports/composables/useSportOptions'
import { useEventRelationSettings } from '@mobile/views/Relation/DetailsPanel/Sports/useEventRelationSettings'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import { useIncidentModals } from '../../../composables/useIncidentModals'
import ShowIncidentsWithSameTimeFilterSwitch from '../Filters/Switches/ShowIncidentsWithSameTimeFilterSwitch.vue'
import IncidentsPerPageSelect from '../Selects/IncidentsPerPageSelect.vue'
import SettingsSelect from '../Selects/SettingsSelect/SettingsSelect.vue'
import ToggleSwitchWithLabel from '../Toggles/ToggleSwitchWithLabel.vue'
import { SelectConfig } from './types'

// composables
const eventInjections = useEventInjections()
const { probableEvent, sport } = eventInjections
const sportOptionsComposable = useSportOptions(sport)
const {
  hideIncidentsOptions,
  participantIncidentsOptions,
  detailsIncidentsOptions,
} = sportOptionsComposable
const { eventParticipantsSelectOptions } =
  resolveEventSelectOptions(probableEvent)
const {
  incidentType,
  hideIncidents,
  detailsIncidents,
  participantsIncidents,
  enableFilters,
  emptyIncidents,
  eventParticipants,
} = useEventRelationSettings(eventInjections, sportOptionsComposable)

const { openIncidentAddModal } = useIncidentModals()

const selectConfigs = computed<SelectConfig[]>(() => [
  {
    key: 'hideIncidents',
    label: 'Hide incidents',
    modelValue: hideIncidents,
    options: hideIncidentsOptions,
    mode: 'multiple',
    trackBy: 'name',
    searchable: true,
    customLabelSlot: true,
    dataTestId: 'details-panel-hide-incidents-select',
  },
  {
    key: 'participantsIncidents',
    label: 'Participants incidents',
    modelValue: participantsIncidents,
    options: participantIncidentsOptions,
    mode: 'multiple',
    trackBy: 'name',
    searchable: true,
    customLabelSlot: true,
    dataTestId: 'details-panel-participants-incidents-select',
  },
  {
    key: 'detailsIncidents',
    label: 'Details incidents',
    modelValue: detailsIncidents,
    options: detailsIncidentsOptions,
    mode: 'multiple',
    trackBy: 'name',
    searchable: true,
    customLabelSlot: true,
    dataTestId: 'details-panel-details-incidents-select',
  },
  {
    key: 'incidentType',
    label: 'Incident type',
    modelValue: incidentType,
    options: participantIncidentsOptions,
    mode: 'multiple',
    dataTestId: 'details-panel-incident-type-select',
  },
  {
    key: 'eventParticipants',
    label: 'Participant',
    modelValue: eventParticipants,
    options: eventParticipantsSelectOptions,
    clearable: true,
    dataTestId: 'details-panel-participant-select',
  },
])
</script>

<template>
  <div class="px-4 pb-4">
    <div class="flex flex-wrap items-end gap-4">
      <SettingsSelect
        v-for="config in selectConfigs"
        :key="config.key"
        v-model="config.modelValue.value"
        class="w-[200px]"
        :label="config.label"
        :mode="config.mode"
        :trackBy="config.trackBy"
        :searchable="config.searchable"
        :customLabelSlot="config.customLabelSlot"
        :clearable="config.clearable"
        :options="toValue(config.options)"
        :dataTestId="config.dataTestId"
      />
      <ToggleSwitchWithLabel
        v-model="emptyIncidents"
        label="Empty incidents"
        data-testid="empty-incidents-switch"
      />
      <ShowIncidentsWithSameTimeFilterSwitch
        v-if="probableEvent.sport_id === SportId.Soccer"
      />
      <ToggleSwitchWithLabel
        v-model="enableFilters"
        label="Enable filters"
      />
      <Button
        size="xs"
        color="blue"
        data-testid="manual-add-incident-button"
        @click="openIncidentAddModal"
      >
        Manual add incident
      </Button>
      <IncidentsPerPageSelect class="w-[200px]" />
    </div>
  </div>
</template>
